import { createContext, useContext, useRef, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import exportService from "../services/exportService";

const ExportContext = createContext({ startExportPolling: () => {} });

export const ExportProvider = ({ children }) => {
  const pollingRefs = useRef({});
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
    setTimeout(() => setToast((prev) => ({ ...prev, open: false })), 4000);
  };

  const startExportPolling = async (job_id, label, callbacks = {}) => {
    const { onComplete, onError } = callbacks;

    const stopPolling = () => {
      if (pollingRefs.current[job_id]) {
        clearInterval(pollingRefs.current[job_id]);
        delete pollingRefs.current[job_id];
      }
    };

    const handleResolved = (statusData) => {
      if (statusData.status === "completed") {
        stopPolling();
        window.location.href = `${process.env.REACT_APP_API_URL}/download/${job_id}`;
        showToast(`${label} exported successfully. Please check your browser's downloads.`);
        onComplete?.();
        return true;
      }
      if (statusData.status === "failed") {
        stopPolling();
        showToast(statusData.error_msg || `Export failed for ${label}`, "error");
        onError?.();
        return true;
      }
      return false;
    };

    const pollStatus = async () => {
      try {
        const statusData = await exportService.getExportStatus(job_id);
        handleResolved(statusData);
      } catch {
        stopPolling();
        showToast(`Export failed for ${label}`, "error");
        onError?.();
      }
    };

    // Immediate check right after trigger
    try {
      const immediateStatus = await exportService.getExportStatus(job_id);
      if (handleResolved(immediateStatus)) return;
    } catch {
      // ignore, fall through to interval polling
    }

    // Still pending — poll every 3 seconds
    pollingRefs.current[job_id] = setInterval(pollStatus, 3000);
  };

  return (
    <ExportContext.Provider value={{ startExportPolling }}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ExportContext.Provider>
  );
};

export const useExport = () => useContext(ExportContext);
